import { Card, Col, Empty, Row } from 'antd';
import { useEffect, useRef } from 'react';
import type { CabinCard } from '@/services/cabinConfig';
import styles from './DynamicModuleCards.module.css';

interface Props {
  cards: CabinCard[];
  onCardClick: (card: CabinCard) => void;
  emptyMessage?: string;
}

function DynamicModuleCards({ cards, onCardClick, emptyMessage = '暂无配置卡片' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const updateCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.offsetWidth * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
      ctx.scale(dpr, dpr);
    };
    setTimeout(updateCanvasSize, 100);

    let animationFrame: number;
    let offset = 0;

    const drawLines = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      ctx.clearRect(0, 0, width, height);
      const els = container.querySelectorAll('[data-module-card]');
      const positions: { x: number; y: number }[] = [];
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        positions.push({
          x: rect.left - cRect.left + rect.width / 2,
          y: rect.top - cRect.top + rect.height / 2,
        });
      });
      if (positions.length > 1) {
        ctx.strokeStyle = `rgba(0, 255, 255, ${0.2 + Math.sin(offset * 0.05) * 0.08})`;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([8, 6]);
        ctx.lineDashOffset = -offset;
        for (let i = 0; i < positions.length - 1; i++) {
          if (Math.abs(positions[i].y - positions[i + 1].y) < 50) {
            ctx.beginPath();
            ctx.moveTo(positions[i].x, positions[i].y);
            ctx.lineTo(positions[i + 1].x, positions[i + 1].y);
            ctx.stroke();
          }
        }
      }
      offset += 1;
      animationFrame = requestAnimationFrame(drawLines);
    };
    setTimeout(drawLines, 150);

    const handleResize = () => updateCanvasSize();
    window.addEventListener('resize', handleResize);
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', handleResize);
    };
  }, [cards]);

  if (cards.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description={<span style={{ color: 'rgba(255,255,255,0.35)' }}>{emptyMessage}</span>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className={styles.modulesContainer} ref={containerRef}>
      <canvas ref={canvasRef} className={styles.connectionCanvas} />
      <Row gutter={[20, 20]}>
        {cards.map((card) => (
          <Col key={card.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              className={styles.moduleCard}
              hoverable={false}
              onClick={() => onCardClick(card)}
              data-module-card
            >
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{card.name}</h3>
                {card.desc && <p className={styles.cardDesc}>{card.desc}</p>}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

export default DynamicModuleCards;
